import { useApolloClient } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { RiIndeterminateCircleLine, RiSearchEyeLine } from "react-icons/ri";
import { Document } from "src/components/DocumentViewer/V2";
import { DocumentViewerModal } from "src/components/DocumentViewer/V2/DocumentViewerModal";
import { DELETE_DOCUMENT_ITEM } from "src/components/graphql/mutations";
import { GET_DOCUMENT_INFO } from "src/components/graphql/queries";
import useAccessToken from "src/hooks/useAccessToken";
import {
  DocumentStorageStatus,
  useDocumentStorage,
} from "src/hooks/useDocumentStorage";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { Status } from "src/types/authData";
import * as GQL from "src/types/graphql";
import { ConfirmationDialog } from "../../Dialogs/ConfirmationDialog";
import { DocumentViewer } from "../../DocumentViewer";
import { TextWithOverflowTooltip } from "../../TextWithOverflowTooltip";
interface QuestionFileItemProps {
  documentId: string;
  viewMode?: boolean;
  onDeleteDocument: (documentId: string) => void;
}

export const QuestionFileItem: React.FC<QuestionFileItemProps> = ({
  documentId,
  viewMode = false,
  onDeleteDocument,
}) => {
  const toast = useToast();
  const accessToken = useAccessToken();
  const apolloClient = useApolloClient();
  const [previewFile, setPreviewFile] = useState<boolean>(false);
  const [documentUrl, setDocumentUrl] = useState<string | undefined>();
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const [fileToDelete, setFileToDelete] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  // Just-uploaded documents are primed into the Apollo cache by
  // UploadFileQuestion, so the filename can render on first paint.
  const [documentInfo, setDocumentInfo] =
    useState<GQL.GetDocumentoInfo_document_metadata_by_pk | null>(
      () =>
        apolloClient.readQuery<
          GQL.GetDocumentoInfo,
          GQL.GetDocumentoInfoVariables
        >({
          query: GET_DOCUMENT_INFO,
          variables: { document_id: documentId },
        })?.document_metadata_by_pk ?? null
    );

  const [deleteDocumentItem] = useRemoteDataMutation<
    GQL.DeleteDocumentItem,
    GQL.DeleteDocumentItemVariables
  >(DELETE_DOCUMENT_ITEM);

  const { getDownloadUrl, getDocumentInfo } = useDocumentStorage();

  useEffect(() => {
    const handleDocumentInfo = async () => {
      if (!documentId) return;

      const info = await getDocumentInfo(documentId);
      setDocumentInfo(info ?? null);
    };

    handleDocumentInfo();
  }, [documentId, getDocumentInfo]);

  const handleClickOnFilePreview = async () => {
    // Download URL is fetched lazily on first preview, then reused.
    if (documentUrl) {
      setPreviewFile(true);
      return;
    }

    if (accessToken.status !== Status.OK) return;

    setIsLoadingPreview(true);
    try {
      const url = await getDownloadUrl(documentId);
      if (url.status === DocumentStorageStatus.OK) {
        setDocumentUrl(url.data);
        setPreviewFile(true);
      }
    } catch (err: unknown) {
      console.error(err);
      toast({
        title: "Error loading preview",
        description:
          "Please try again later or report the problem to our support team.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const toggleConfirmationDialog = () => {
    setFileToDelete(true);
  };

  const handleDeleteFile = async () => {
    if (!documentId) return;

    setIsDeleting(true);
    try {
      await deleteDocumentItem({
        variables: {
          document_id: documentId,
        },
      });

      setFileToDelete(false);
      onDeleteDocument(documentId);
    } catch (err: unknown) {
      console.error(err);
      toast({
        title: "Error deleting document",
        description:
          "Please try again later or report the problem to our support team.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const displayFilename = documentInfo?.filename;

  const document: Document = React.useMemo(
    () => ({
      type: "url",
      url: documentUrl ?? "",
      fileName: displayFilename ?? "",
    }),
    [displayFilename, documentUrl]
  );

  const isV2Supported = DocumentViewer.useIsV2Supported();

  return (
    <>
      {displayFilename ? (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Button
            variant="link"
            textAlign="left"
            onClick={handleClickOnFilePreview}
            textDecoration="none"
            color="primary.500"
            fontWeight="normal"
            display="flex"
            alignItems="center"
            flex="1"
            minWidth="0"
          >
            <TextWithOverflowTooltip
              content={displayFilename || ""}
              hasArrow
              flex="1"
              minWidth="0"
              maxWidth="100%"
            />
          </Button>

          <Tooltip label="Preview file" hasArrow>
            <IconButton
              variant="link"
              icon={<RiSearchEyeLine color="primary.500" />}
              onClick={handleClickOnFilePreview}
              isLoading={isLoadingPreview}
              aria-label="Preview file"
              minWidth="unset"
            />
          </Tooltip>

          {!viewMode && (
            <Tooltip label="Delete file" hasArrow>
              <IconButton
                variant="link"
                icon={<RiIndeterminateCircleLine color="red" />}
                onClick={toggleConfirmationDialog}
                aria-label="Delete file"
                minWidth="unset"
              />
            </Tooltip>
          )}
        </Box>
      ) : (
        <Flex color="gray.600" fontSize="sm" alignItems="center" gap={1}>
          <Spinner size="xs" />
          <Text>Loading...</Text>
        </Flex>
      )}

      {isV2Supported ? (
        <DocumentViewerModal
          isOpen={previewFile}
          onClose={() => setPreviewFile(false)}
          document={document}
        />
      ) : (
        <DocumentViewer.V1
          isOpen={!!previewFile}
          uri={documentUrl ?? ""}
          title={displayFilename ?? ""}
          onClose={() => setPreviewFile(false)}
        />
      )}

      <ConfirmationDialog
        isOpen={fileToDelete}
        header="Delete file?"
        body="This action can't be undone."
        onCancel={() => {
          if (!isDeleting) setFileToDelete(false);
        }}
        onConfirm={handleDeleteFile}
        cancelButton={{ label: "No, cancel", isDisabled: isDeleting }}
        confirmButton={{
          label: "Yes, delete",
          colorScheme: "red",
          isLoading: isDeleting,
        }}
      />
    </>
  );
};
